import React, { useEffect, useState } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { COMMAND_PRIORITY_LOW } from 'lexical';
import { KEY_MODIFIER_COMMAND } from 'lexical';
import './scss/KeyboardShortcutsPanel.scss';

const SHORTCUTS = [
  {
    category: 'Text Formatting',
    shortcuts: [
      { keys: ['Cmd', 'B'], description: 'Bold' },
      { keys: ['Cmd', 'I'], description: 'Italic' },
      { keys: ['Cmd', 'U'], description: 'Underline' },
      { keys: ['Cmd', 'Shift', 'S'], description: 'Strikethrough' },
      { keys: ['Cmd', 'E'], description: 'Inline Code' },
      { keys: ['Cmd', 'K'], description: 'Insert Link' },
    ],
  },
  {
    category: 'Block Formatting',
    shortcuts: [
      { keys: ['Cmd', 'Alt', '1'], description: 'Heading 1' },
      { keys: ['Cmd', 'Alt', '2'], description: 'Heading 2' },
      { keys: ['Cmd', 'Alt', '3'], description: 'Heading 3' },
      { keys: ['Cmd', 'Shift', '7'], description: 'Numbered List' },
      { keys: ['Cmd', 'Shift', '8'], description: 'Bulleted List' },
      { keys: ['Cmd', 'Shift', '9'], description: 'Quote' },
    ],
  },
  {
    category: 'Alignment',
    shortcuts: [
      { keys: ['Cmd', 'Shift', 'L'], description: 'Align Left' },
      { keys: ['Cmd', 'Shift', 'E'], description: 'Align Center' },
      { keys: ['Cmd', 'Shift', 'R'], description: 'Align Right' },
      { keys: ['Cmd', 'Shift', 'J'], description: 'Justify' },
    ],
  },
  {
    category: 'Actions',
    shortcuts: [
      { keys: ['Cmd', 'Z'], description: 'Undo' },
      { keys: ['Cmd', 'Shift', 'Z'], description: 'Redo' },
      { keys: ['/'], description: 'Open Command Menu' },
      { keys: ['Cmd', '/'], description: 'Toggle Shortcuts Panel' },
      { keys: ['Esc'], description: 'Close Menus' },
    ],
  },
  {
    category: 'Block Management',
    shortcuts: [
      { keys: ['Drag Handle'], description: 'Hover over any block to see drag handle' },
      { keys: ['Click & Drag'], description: 'Drag blocks to reorder them' },
    ],
  },
];

function KeyboardShortcutsPanel() {
  const [editor] = useLexicalComposerContext();
  const [isOpen, setIsOpen] = useState(false);
  const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform);

  useEffect(() => {
    const handleKeyDown = (event) => {
      // Cmd+/ or Ctrl+/ to toggle panel
      if ((event.metaKey || event.ctrlKey) && event.key === '/') {
        event.preventDefault();
        setIsOpen((prev) => !prev);
      }
      // Esc to close
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const formatKey = (key) => {
    if (key === 'Cmd') return isMac ? '⌘' : 'Ctrl';
    if (key === 'Alt') return isMac ? '⌥' : 'Alt';
    if (key === 'Shift') return isMac ? '⇧' : 'Shift';
    return key;
  };

  return (
    <div className="keyboard-shortcuts-overlay" onClick={() => setIsOpen(false)}>
      <div className="keyboard-shortcuts-panel" onClick={(e) => e.stopPropagation()}>
        <div className="keyboard-shortcuts-header">
          <h2>Keyboard Shortcuts</h2>
          <button className="keyboard-shortcuts-close" onClick={() => setIsOpen(false)}>
            ✕
          </button>
        </div>
        <div className="keyboard-shortcuts-content">
          {SHORTCUTS.map((section) => (
            <div key={section.category} className="keyboard-shortcuts-section">
              <h3>{section.category}</h3>
              <div className="keyboard-shortcuts-list">
                {section.shortcuts.map((shortcut, index) => (
                  <div key={index} className="keyboard-shortcut-item">
                    <span className="keyboard-shortcut-description">{shortcut.description}</span>
                    <div className="keyboard-shortcut-keys">
                      {shortcut.keys.map((key, i) => (
                        <React.Fragment key={i}>
                          <kbd className="keyboard-shortcut-key">{formatKey(key)}</kbd>
                          {i < shortcut.keys.length - 1 && <span className="keyboard-shortcut-plus">+</span>}
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="keyboard-shortcuts-footer">
          Press <kbd>Cmd</kbd> + <kbd>/</kbd> to toggle this panel
        </div>
      </div>
    </div>
  );
}

export default KeyboardShortcutsPanel;

